import { Link } from "react-router-dom";
import { ArrowRight, MessageCircleHeart, ShieldCheck, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { FadeUp, Tilt, EASE } from "../site/motion";
import { ConceptBadge } from "../site/Badges";
import { M } from "../../data/site";

const PRINCIPLES = [
  { icon: ShieldCheck, t: "Honest by design", d: "Every suggestion comes with the reason behind it — never a black box." },
  { icon: MessageCircleHeart, t: "Warm, never pushy", d: "She guides gently and respects how your family already lives." },
  { icon: Sparkles, t: "Grows with you", d: "From tonight’s dinner to lifelong habits, she learns what matters to you." },
];

const Chip = ({ children, className, delay }) => (
  <motion.div initial={{ opacity: 0, scale: 0.85 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.8, ease: EASE, delay }}
    style={{ transform: "translateZ(60px)" }} className={`glass absolute rounded-2xl px-4 py-3 text-sm text-white ${className}`}>{children}</motion.div>
);

export const MeetHonesti = () => (
  <section id="meet-honesti" data-testid="meet-honesti-section" className="relative mx-auto grid max-w-[1440px] items-center gap-16 px-5 py-24 lg:grid-cols-12 lg:px-12 lg:py-36">
    <div className="pointer-events-none absolute left-0 top-1/3 h-[480px] w-[480px] rounded-full bg-blue-600/20 blur-[140px]" />
    <FadeUp className="relative lg:col-span-5">
      <Tilt max={7} className="mx-auto max-w-md">
        <div className="relative">
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2.2rem] glow-edge">
            <img src={M("honesti.jpg")} alt="Portrait of Honesti in her electric-blue holographic suit with the HC emblem" className="h-full w-full object-cover" />
            <div className="scanline pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-transparent via-cyan-300/10 to-transparent" />
          </div>
          <Chip className="-left-4 top-10 sm:-left-10" delay={0.3}><span className="eyebrow !text-[0.6rem] block">Knows</span>your household</Chip>
          <Chip className="-right-3 top-1/2 sm:-right-10" delay={0.45}><span className="eyebrow !text-[0.6rem] block">Explains</span>the “why”</Chip>
          <Chip className="bottom-8 left-6" delay={0.6}><span className="eyebrow !text-[0.6rem] block">Learns</span>alongside you</Chip>
        </div>
      </Tilt>
    </FadeUp>
    <div className="relative lg:col-span-6 lg:col-start-7">
      <FadeUp>
        <p className="eyebrow mb-6">01 — Meet Honesti</p>
        <h2 className="font-display text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">Warm like family.<br /><span className="text-grad">Clear like a good friend.</span></h2>
        <p className="mt-7 text-base leading-relaxed text-slate-300 md:text-lg">
          Honesti is the intelligent companion at the heart of HonestChoice AI. She is being designed to help families make more confident everyday decisions — starting in the kitchen, where so many of those decisions happen.
        </p>
        <div className="mt-5"><ConceptBadge label="In development" /></div>
      </FadeUp>
      <div className="mt-12 space-y-3">
        {PRINCIPLES.map((p, i) => (
          <FadeUp key={p.t} delay={i * 0.1}>
            <div className="group flex gap-5 rounded-2xl border border-cyan-400/15 bg-white/[0.02] p-5 transition-[border-color,background-color] duration-300 hover:border-cyan-300/50 hover:bg-blue-900/20">
              <p.icon className="mt-1 h-6 w-6 shrink-0 text-cyan-300" strokeWidth={1.5} />
              <div><p className="font-display text-xl font-semibold text-white">{p.t}</p><p className="mt-1 text-sm text-slate-300">{p.d}</p></div>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp className="mt-10"><Link to="/features" data-testid="meet-explore-features-btn" className="btn-primary">Explore what she can do <ArrowRight className="h-4 w-4" /></Link></FadeUp>
    </div>
  </section>
);
